import moment from "moment";
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import * as Yup from "yup";
import Input from "components/Input";
import Button from "components/Button";
import SelectStyled from "components/Select";
import LANGS from "global/lang";
import {
  getExternalLinkById,
  addExternalLink,
  editExternalLink,
  deleteMultimedia,
  addMultimedias,
  deleteExternalLink,
} from "services/services";
import { languagesSelectList } from "../ExternalLinkContainer";
import {
  DatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import { useStyles } from "./styles";
import {
  FormControl,
  InputLabel,
  Grid,
  TextField,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import DialogAlert from "../../../components/DialogAlert";
import Delete from "@material-ui/icons/Delete";
import { EXTERNAL_LINK_EDIT, EXTERNAL_LINK } from "navigation/CONSTANTS";
import { humanizeProps } from "helpers/humanizeResponse";
import { getUser } from "services/authService";

const dayList = [
  { value: "everyday", display: "Todos los Días" },
  { value: "monday", display: "Lunes" },
  { value: "tuesday", display: "Martes" },
  { value: "wednesday", display: "Miércoles" },
  { value: "thursday", display: "Jueves" },
  { value: "friday", display: "Viernes" },
  { value: "saturday", display: "Sábado" },
  { value: "sunday", display: "Domingo" }
];

const schemaExternalLink = Yup.object().shape({
    title: Yup.string()
      .required()
      .test("required", "es requerido", (val) => !!val?.trim().length),
    url: Yup.string()
      .url("Debe ser un enlace válido")
      .required()
      .test("required", "es requerido", (val) => !!val?.trim().length),
    language: Yup.string()
      .required()
      .test("required", "es requerido", (val) => String(val).length === 2),
    from: Yup.date().required(),
    to: Yup.date().required(),
    periodicity: Yup.number().required(),
    moment: Yup.string().required(),
  });

export function ExternalLinkEdit() {
  const { id } = useParams();
  const initialExternalValue = {
    title: "",
    url: "",
    from: moment().toISOString(),
    to: moment().toISOString(),
    language: 0,
    moment: "MOR",
    periodicity: 0,
  };

  const [externalEdit, setExternalEdit] = useState(initialExternalValue);
  const [externalLang, setExternalLang] = useState(0);
  const [saving, setSaving] = useState(false);
  const [multimedia, setMultimedia] = useState({ name: "" });
  const [error, setError] = useState({ msgType: "", message: "", show: false });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subtitle: "",
  });

  const user = getUser();
  const history = useHistory();
  const classes = useStyles();

  useEffect(() => {
    if (Number(id)) fetchExternalLink(id);
  }, [id]);

  const languages = languagesSelectList();
  languages[0].display = "--Selecionar--";
  const isEditing = !!Number(id);
  const isAdmin = user?.role.description === "ADMIN";

  async function fetchExternalLink(idExternalLink) {
    const res = await getExternalLinkById(idExternalLink);
    if (res) {
      const p = {
        id: res.id,
        title: res.title,
        url: res.url,
        language: res.language.code,
        from: moment(res.from).toISOString(),
        to: moment(res.to).toISOString(),
        periodicity: res.periodicity,
        moment: res.moment?.code,
      };
      setExternalEdit(p);
      setExternalLang(res.language.id);
      if (res.multimedia[0]) {
        setMultimedia({
          id: res.multimedia[0].id,
          name: res.multimedia[0].original_name,
          type: res.multimedia[0].type.code,
          url: res.multimedia[0].url,
        });
      }
    }
  }

  function handleBack() {
    history.push(EXTERNAL_LINK);
  }

  function onChangeForm({ name, value }) {
    const edit = { ...externalEdit, [name]: value };
    schemaExternalLink
      .validate(edit)
      .catch((e) => {
        setError({
          message: humanizeProps(`${e.path} ${e.message}`),
          msgType: "msgError",
        });
      })
      .finally(() => {
        setExternalEdit(edit);
      });
  }

  function onChangeLang(e) {
    const lang = LANGS.find((l) => l.id == e.target.value) || { id: 0, code: "" };
    setExternalLang(lang.id);
    onChangeForm({ name: "language", value: lang.code });
  }
  const handlePickerChange = (date, key) => {
    onChangeForm({ name: key, value: moment(date).toISOString() });
  };

  function handlerUploadFile(event) {
    const file = event.target.files[0];
    setMultimedia((prev) => ({ ...prev, name: file.name, file }));
  }

  async function onSaveExternalLink() {
    const service = isEditing ? editExternalLink : addExternalLink;
    setSaving(true);
    let result = { show: true, message: "Datos guardados" };
    let id_external_link = 0;
    service(externalEdit)
      .then((data) => {
        id_external_link = Array.isArray(data) ? data[0] : data.id;
        setError({
          show: true,
          message: "Guardando imagenes... Por favor espere.",
        });
        return multimedia.file || multimedia.delete
          ? saveMultimedias(id_external_link)
          : Promise.resolve();
      })
      .catch((e) => {
        result.message = humanizeProps(e.message);
        result.msgType = "msgError";
      })
      .finally(() => {
        setSaving(false);
        setError(result);
        if (id_external_link) history.push(`${EXTERNAL_LINK_EDIT}/${id_external_link}`);
      });
  }

  async function saveMultimedias(owner, ownerType = "EXT") {
    const formData = new FormData();
    const metadata = {
      name: multimedia.name,
      type: "IMG",
      owners: [{ owner, ownerType }],
    };
    if (!isEditing && multimedia.file) {
      formData.append(multimedia.name, multimedia.file);
      formData.append("metadata", JSON.stringify([metadata]));

      return addMultimedias(formData);
    } else if (multimedia.file) {
      metadata.replace = multimedia.id;
      formData.append(multimedia.name, multimedia.file);
      formData.append("metadata", JSON.stringify([metadata]));

      return addMultimedias(formData);
    } else if (multimedia.delete) {
      return deleteMultimedia([
        { multimedia_id: multimedia.id, owner, ownerType },
      ]);
    }
  }

  const handleInputChange = (event) => {
    const edit = { ...externalEdit, [event.target.name]: event.target.value };
    setExternalEdit(edit);
  };

  return (
    <div data-testid="modal_external_link">
      <Grid container data-testid="external_link-edit-view">
        {error?.show && (
          <div className={`${classes.messageBox} ${classes[error.msgType]}`}>
            {error.message}
          </div>
        )}
        <Grid item xs={9} style={{ padding: 15 }}>
          <Grid container>
            <Grid item xs={6}>
              <div>
                <h2>Enlaces Externos - Oraciones diarias</h2>
                {!isEditing && <h5>Nuevo Enalce Externo - Oraciones diarias</h5>}
              </div>
            </Grid>
          </Grid>

          <FormControl variant="filled" size="small" margin="normal">
            <InputLabel style={{ color: '#444C67' }}>Idioma</InputLabel>
            <SelectStyled
              items={languages}
              handlerOnChange={onChangeLang}
              defaultValue={externalLang}
              disabled={!isAdmin}
              style={{ color: '#444C67' }}
              inputProps={{ style: { color: '#444C67' } }}
            />
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input
                isSucess={!!externalEdit.url}
                isError={!externalEdit.url}
                name="url"
                label="URL"
                isRequired
                id="txt_url_link"
                onChange={(e) => onChangeForm(e.target)}
                value={externalEdit.url || ""}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                isSucess={!!externalEdit.title}
                isError={!externalEdit.title}
                name="title"
                label="Título"
                isRequired
                id="txt_title_link"
                onChange={(e) => onChangeForm(e.target)}
                value={externalEdit.title || ""}
              />
            </Grid>
          </Grid>
          <FormControl variant="filled" size="small" margin="normal" fullWidth>
            <div style={{ display: 'flex', alignItems: 'start', marginBottom: '10px', flexDirection: 'column' }}>
              {multimedia.file || multimedia.name ? (
                <>
                  <div style={{ width: '40px', height: '40px', marginRight: '10px', overflow: 'hidden' }}>
                    {multimedia.url || multimedia.file ? (
                      <img
                        src={multimedia.file ? URL.createObjectURL(multimedia.file) : multimedia.url}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ width: '40px', height: '40px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>IMG</span>
                      </div>
                    )}
                  </div>
                  <div style={{ marginRight: '10px', flex: 1 }}>
                    <p className={classes.imgLabel}> ICONO ({multimedia.name} - TAMAÑO RECOMENDADO 24 X 24 PX)</p>
                  </div>
                </>
              ) : null}
              <label htmlFor="input_file">
                <Button
                  id="input_multimedia_external_link"
                  variant="contained"
                  component="span"
                  text={multimedia.name ? "Cambiar imagen" : "Seleccionar imagen"}
                  css={classes.btnInput}
                />
              </label>
            </div>
          </FormControl>
          <input
            accept=".png, .jpg, .svg"
            id="input_file"
            name="input_file"
            hidden
            type="file"
            onChange={(event) => {
              handlerUploadFile(event);
              event.target.value = "";
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
                <DatePicker
                  openTo="month"
                  format="dd-MM-yyyy"
                  label="Desde (Fecha)"
                  views={["year", "month", "date"]}
                  value={moment(externalEdit.from).toISOString()}
                  onChange={(e) => handlePickerChange(e, 'from')}
                  inputVariant="outlined"
                  fullWidth
                  required
                  InputLabelProps={{
                    shrink: true,
                    className: classes.input
                  }}
                  InputProps={{
                    className: classes.input,
                    style: { color: '#444C67' }
                  }}
                  margin="normal"
                  name="from"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { color: '#444C67' }
                      }}
                    />
                  )}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
                <DatePicker
                  openTo="month"
                  format="dd-MM-yyyy"
                  label="Hasta (Fecha)"
                  views={["year", "month", "date"]}
                  value={moment(externalEdit.to).toISOString()}
                  onChange={(e) => handlePickerChange(e, 'to')}
                  inputVariant="outlined"
                  fullWidth
                  required
                  InputLabelProps={{
                    shrink: true,
                    className: classes.input
                  }}
                  InputProps={{
                    className: classes.input,
                    style: { color: '#444C67' }
                  }}
                  margin="normal"
                  name="to"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { color: '#444C67' }
                      }}
                    />
                  )}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputLabel>Día</InputLabel>
              <FormControl variant="outlined" size="small" margin="dense" style={{ marginTop: '5px' }} fullWidth>
                <Select
                  name="periodicity"
                  value={externalEdit.periodicity}
                  onChange={handleInputChange}
                  style={{ height: '56px', color: '#444C67' }}
                  inputProps={{
                    style: {
                      height: '56px',
                      color: '#444C67'
                    },
                    className: classes.input
                  }}
                >
                  {dayList.map((item) => (
                    <MenuItem key={`day-${item.value}`} value={item.value}>
                      <Typography style={{ color: '#444C67' }}>{item.display}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  className={classes.label}
                >
                  Momento
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="moment"
                  name="moment"
                  value={externalEdit.moment || "MOR"}
                  onChange={handleInputChange}
                  className={classes.radioGroup}
                >
                  <FormControlLabel
                    value="MOR"
                    control={
                      <Radio
                        style={{ display: 'none' }}
                      />
                    }
                    label="Mañana"
                    className={`${classes.radioButton} ${externalEdit.moment === "MOR" ? classes.radioButtonSelected : classes.radioButtonUnselected}`}
                  />
                  <FormControlLabel
                    value="AFT"
                    control={
                      <Radio
                        style={{ display: 'none' }}
                      />
                    }
                    label="Tarde"
                    className={`${classes.radioButton} ${externalEdit.moment === "AFT" ? classes.radioButtonSelected : classes.radioButtonUnselected}`}
                  />
                  <FormControlLabel
                    value="EVE"
                    control={
                      <Radio
                        style={{ display: 'none' }}
                      />
                    }
                    label="Noche"
                    className={`${classes.radioButton} ${externalEdit.moment === "EVE" ? classes.radioButtonSelected : classes.radioButtonUnselected}`}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3} style={{ padding: "30px 0" }}>
          <Button
            text={!saving ? "Guardar" : "Procesando...."}
            disabled={saving}
            type="primary"
            onClick={onSaveExternalLink}
            style={{ marginRight: 10 }}
            data-testid="btn-save-external_link-edit"
          />
          <Button
            text="Volver"
            onClick={handleBack}
            id="btn_back_external_linkEdit"
            data-testid="btn_back_external_linkEdit"
            variant="outlined"
          />
          {isEditing && isAdmin && (
            <Button
              text="Eliminar"
              onClick={() => {
                setConfirmDialog({
                  isOpen: true,
                  title: "¿Desea eliminar este registro?",
                  subtitle: "Eliminar",
                  onConfirm: () => {
                    deleteExternalLink(id);
                    history.push(EXTERNAL_LINK);
                  },
                });
              }}
              id="btn_del_external_link"
              data-testid="btn_del_external_link"
              variant="text"
              color="secondary"
              icon={<Delete />}
              style={{ float: "right" }}
            />
          )}
        </Grid>
        <DialogAlert
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
        />
      </Grid>
    </div>
  );
}
